/**
 * Configuración de la aplicación
 * Aquí se definen las funcionalidades y secciones disponibles en la app
 */

export interface AppFeature {
  enabled: boolean;
  icon?: string;
  label?: string;
  order?: number;
}

export interface NetworkingField {
  key: string;
  label: string;
  type: 'text' | 'email' | 'phone' | 'url';
  required: boolean;
  visible: boolean;
  order: number;
}

export interface AppConfig {
  // Funcionalidades principales
  features: {
    schedule: AppFeature;
    speakers: AppFeature;
    questions: AppFeature;
    favorites: AppFeature;
    calendar: AppFeature;
    badge: AppFeature;
    networking: AppFeature;
    map: AppFeature;
    about: AppFeature;
    carnetDigital: AppFeature & {
      mandatoryEventCode?: string; // Código del evento mandatorio para consultas del carnet digital
    };
  };

  // Configuración de autenticación
  auth: {
    multievent: boolean; // Permite login con múltiples eventos
    client: string; // ID del cliente por defecto
    defaultEventID?: string; // ID del evento por defecto cuando multievent es false
    defaultRoute: string; // Ruta a la que se redirige después de seleccionar un evento o cerrar la selección
    showPasswordAsText: boolean; // Si true, muestra la contraseña como texto visible; si false, como password oculto
  };

  // Configuración de networking
  networking: {
    enabled: boolean;
    qrCodePrefix: string; // Prefijo para identificar QRs de la app
    fields: NetworkingField[];
    maxScansPerUser: number;
    allowDuplicateScans: boolean;
  };

  // Configuración de tabs del menú inferior
  bottomTabs: {
    schedule: AppFeature;
    networking: AppFeature;
    speakers: AppFeature;
    badge: AppFeature;
  };

  // Configuración de preguntas
  questions: {
    enabled: boolean;
    allowAnonymous: boolean;
    requireModeration: boolean;
    maxQuestionsPerUser: number;
  };

  // Configuración de favoritos
  favorites: {
    enabled: boolean;
    syncWithFirebase: boolean;
    maxFavorites: number;
  };
}

// Configuración por defecto de la aplicación
export const defaultAppConfig: AppConfig = {
  features: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    questions: {
      enabled: true,
      icon: 'help-circle-outline',
      label: 'Preguntas',
      order: 3
    },
    favorites: {
      enabled: true,
      icon: 'heart-outline',
      label: 'Favoritos',
      order: 4
    },
    calendar: {
      enabled: true,
      icon: 'calendar',
      label: 'Calendario',
      order: 5
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 6
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 7
    },
     speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Conferencistas',
      order: 2
    },
    map: {
      enabled: true,
      icon: 'map-outline',
      label: 'Mapa',
      order: 8
    },
    about: {
      enabled: true,
      icon: 'information-circle-outline',
      label: 'Acerca de',
      order: 9
    },
    carnetDigital: {
      enabled: true,
      icon: 'card-outline',
      label: 'Carnet Digital',
      order: 10,
      mandatoryEventCode: '06964DCE' // Código del evento base para consultas del carnet digital
    }
  },

  // Configuración de autenticación
  auth: {
    multievent: true, // Habilitar soporte para múltiples eventos
    client: '31', // Cliente por defecto
    // defaultEventID: '06964DCE', // Dejar comentado para mostrar lista de eventos cuando multievent es true
    defaultRoute: '/tabs/schedule', // Ruta por defecto después de seleccionar evento (ej: '/tabs/schedule', '/tabs/speakers', '/account')
    showPasswordAsText: false // Por defecto, ocultar la contraseña (false = password, true = text)
  },
  
  bottomTabs: {
    schedule: {
      enabled: true,
      icon: 'calendar-outline',
      label: 'Agenda',
      order: 1
    },
    networking: {
      enabled: true,
      icon: 'people-circle-outline',
      label: 'Networking',
      order: 3
    },
    speakers: {
      enabled: true,
      icon: 'people-outline',
      label: 'Speakers',
      order: 2
    },
    badge: {
      enabled: false,
      icon: 'card-outline',
      label: 'Gafete',
      order: 4
    }
  },
  
  networking: {
    enabled: true,
    qrCodePrefix: 'ESIMPOSIO_USER_',
    maxScansPerUser: 500,
    allowDuplicateScans: false,
    fields: [
      {
        key: 'name',
        label: 'Nombre',
        type: 'text',
        required: true,
        visible: true,
        order: 1
      },
      {
        key: 'email',
        label: 'Correo Electrónico',
        type: 'email',
        required: true,
        visible: true,
        order: 2
      },
      {
        key: 'phone',
        label: 'Teléfono',
        type: 'phone',
        required: false,
        visible: true,
        order: 3
      },
      {
        key: 'company',
        label: 'Empresa',
        type: 'text',
        required: false,
        visible: true,
        order: 4
      },
      {
        key: 'position',
        label: 'Cargo',
        type: 'text',
        required: false,
        visible: true,
        order: 5
      },
      {
        key: 'linkedin',
        label: 'LinkedIn',
        type: 'url',
        required: false,
        visible: false,
        order: 6
      }
    ]
  },
  
  questions: {
    enabled: true,
    allowAnonymous: false,
    requireModeration: true,
    maxQuestionsPerUser: 10
  },
  
  favorites: {
    enabled: true,
    syncWithFirebase: true,
    maxFavorites: 50
  }
};

// Función para obtener la configuración (puede ser extendida para cargar desde servidor)
export const getAppConfig = (): AppConfig => {
  // En el futuro, esto puede cargar la configuración desde un servidor
  // Por ahora retorna la configuración por defecto
  return defaultAppConfig;
};

// Función para verificar si una funcionalidad está habilitada
export const isFeatureEnabled = (feature: keyof AppConfig['features']): boolean => {
  const config = getAppConfig();
  return config.features[feature]?.enabled || false;
};

// Función para obtener los campos de networking configurados
export const getNetworkingFields = (): NetworkingField[] => {
  const config = getAppConfig();
  return config.networking.fields
    .filter(field => field.visible)
    .sort((a, b) => a.order - b.order);
};

// Función para obtener los tabs del menú inferior habilitados
export const getEnabledBottomTabs = () => {
  const config = getAppConfig();
  return Object.entries(config.bottomTabs)
    .filter(([_, tab]) => tab.enabled)
    .sort(([_, a], [__, b]) => (a.order || 0) - (b.order || 0))
    .map(([key, tab]) => ({ key, ...tab }));
};

// Función para obtener la configuración de autenticación
export const getAuthConfig = () => {
  const config = getAppConfig();
  return config.auth;
};

// Función para verificar si multievent está habilitado
export const isMultieventEnabled = (): boolean => {
  const config = getAppConfig();
  return config.auth.multievent;
};

// Función para obtener el cliente por defecto
export const getDefaultClient = (): string => {
  const config = getAppConfig();
  return config.auth.client;
};

// Función para obtener el evento por defecto
export const getDefaultEventID = (): string | undefined => {
  const config = getAppConfig();
  return config.auth.defaultEventID;
};
